import React, { useMemo } from 'react';
import { Empty, Space, Table, Tag, Tooltip, Typography } from 'antd';
import type { TableColumnsType as ColumnsType } from 'antd';

import {
  ADDED_BY_PM_CHIP,
  ADDED_BY_PM_TOOLTIP,
  countsLine,
  readCoverage,
} from 'components';

import { AnswerKeyCoverageDocument, AnswerKeyCoverageRow } from './api';
import './blueprintAnswerKey.css';

/**
 * One row per instrument the class can actually trade, and what the answer
 * key does with it.
 *
 * THIS TABLE IS THE FRONT DOOR (D1). Before it, an admin looking at a
 * blueprint could see that an answer key existed and what the validity gate
 * thought of it, and could not see the one thing that decides whether a
 * sitting can be marked: whether every instrument a trainee can put a
 * position on is graded, and where it is not, why. THE WAIT v6 grades ten of
 * thirteen; nothing said so anywhere.
 *
 * THE ROWS ARE THE STARTED-SESSION SHAPE, not the template's asset list. PM
 * injects an FX pair at session start for every currency the template holds
 * an instrument in and cannot pay for, and those injected pairs are exactly
 * the ones a key derived off the template alone will miss. They are labelled
 * so an admin does not have to wonder where GBP= came from.
 *
 * NOT GRADED IS A DECISION, NOT AN ERROR. An option is not graded because the
 * key marks the instrument it is written on; that row says so, and names the
 * column doing the marking. A row with no answer key at all says NO ANSWER
 * KEY YET, which is a different sentence again: nothing has been decided
 * about it.
 */

/** What is left to say when the answer key does not mark this instrument. */
const gradedCell = (row: AnswerKeyCoverageRow, hasKey: boolean) => {
  if (!hasKey) {
    return (
      <Typography.Text type="secondary">No answer key yet</Typography.Text>
    );
  }
  if (row.status === 'graded') {
    return (
      <Space size={4} wrap>
        <Tag color="green">Graded</Tag>
        {row.key_code ? (
          // The leading space lives INSIDE the string: antd's Space lays its
          // children out with a gap, which draws a gap and produces no text
          // node, so the row would read "Graded· US10Y" to anything reading
          // the words rather than looking at them.
          <Typography.Text>{` · ${row.key_code}`}</Typography.Text>
        ) : null}
      </Space>
    );
  }
  // An option is graded THROUGH its underlying: naming that column is the
  // difference between "we skipped it" and "we mark the thing it is written
  // on".
  const through =
    row.reason_kind === 'option' && row.parent_code
      ? ` · option, graded through ${row.parent_code}`
      : '';
  return (
    <Space size={4} wrap>
      <Tag color="gold">Not graded</Tag>
      {through ? <Typography.Text>{through}</Typography.Text> : null}
    </Space>
  );
};

const CoverageTable: React.FC<{
  document: AnswerKeyCoverageDocument | null;
  loading?: boolean;
  /** The page's panel already states the counts; the table repeats them only
   *  when it is standing on its own. */
  showCounts?: boolean;
}> = ({ document: coverageDocument, loading, showCounts = true }) => {
  const hasKey = Boolean(coverageDocument?.key);
  // The counts line says how much of what a class trades is GRADED, so it
  // needs a marking scheme to count against. Without one the rows below say
  // "No answer key yet" one at a time, which is the whole truth there is.
  const coverage = useMemo(
    () => (hasKey ? readCoverage(coverageDocument) : undefined),
    [hasKey, coverageDocument],
  );

  // Not graded first: it is the only part of this table anybody has to read.
  // Within each half the server's order is kept, so the page does not
  // reshuffle under an admin between two loads.
  const rows = useMemo(() => {
    const all = coverageDocument?.rows || [];
    return [
      ...all.filter(row => row.status !== 'graded'),
      ...all.filter(row => row.status === 'graded'),
    ];
  }, [coverageDocument]);

  if (!coverageDocument) {
    return (
      <Empty
        className="bp-coverage-table"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="Nothing has read this variant yet, so there is no per-instrument coverage to show."
      />
    );
  }

  const columns: ColumnsType<AnswerKeyCoverageRow> = [
    {
      title: 'Ticker',
      key: 'ticker',
      width: 220,
      render: (_value, row) => (
        <Space size={4} wrap>
          <Typography.Text strong>{row.ticker || row.name}</Typography.Text>
          {row.injected && (
            <Tooltip title={ADDED_BY_PM_TOOLTIP}>
              <Tag color="blue" aria-label={ADDED_BY_PM_TOOLTIP}>
                {ADDED_BY_PM_CHIP}
              </Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: 'Name',
      key: 'name',
      width: 260,
      render: (_value, row) => (
        <span className="bp-coverage-table__prose">{row.name}</span>
      ),
    },
    {
      title: 'Class',
      key: 'class',
      width: 130,
      render: (_value, row) => (
        <Typography.Text type="secondary">{row.class}</Typography.Text>
      ),
    },
    {
      title: 'Tradable',
      key: 'tradable',
      width: 100,
      render: (_value, row) => (row.tradable === false ? 'No' : 'Yes'),
    },
    {
      title: 'Graded',
      key: 'graded',
      width: 260,
      render: (_value, row) => gradedCell(row, hasKey),
    },
    {
      title: 'Reason',
      key: 'reason',
      render: (_value, row) =>
        row.reason || row.note ? (
          <Space direction="vertical" size={0}>
            {row.reason && (
              <span className="bp-coverage-table__prose">{row.reason}</span>
            )}
            {/* The endpoint's own note. On a graded row it is the whole
                story ("pm-the-wait-v6 grades it as US10Y"); on an injected
                pair it says where the row came from at all. */}
            {row.note && (
              <span className="bp-coverage-table__prose bp-answer-key__muted">
                {row.note}
              </span>
            )}
            {row.reason_kind && (
              <span className="bp-answer-key__muted">{row.reason_kind}</span>
            )}
          </Space>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      title: 'Audit',
      key: 'audit',
      width: 300,
      render: (_value, row) =>
        row.audit ? (
          <Space direction="vertical" size={2}>
            <Tag color={row.audit.verdict === 'pass' ? 'green' : 'gold'}>
              {String(row.audit.verdict || '').toUpperCase()}
            </Tag>
            <span className="bp-coverage-table__prose">{row.audit.detail}</span>
          </Space>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
  ];

  return (
    <div className="bp-coverage-table">
      {showCounts && countsLine(coverage) && (
        <Typography.Paragraph className="bp-answer-key__counts">
          {countsLine(coverage)}
        </Typography.Paragraph>
      )}
      <Table
        rowKey={row => `${row.asset_id ?? 'missing'}-${row.ticker}`}
        loading={loading}
        dataSource={rows}
        columns={columns}
        size="small"
        bordered
        pagination={false}
        scroll={{ x: 'max-content' }}
        rowClassName={row =>
          row.status === 'graded'
            ? ''
            : hasKey
              ? 'bp-coverage-row--not-graded'
              : 'bp-coverage-row--no-key'
        }
      />
    </div>
  );
};

export default CoverageTable;
